import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { ChakraProvider, ColorModeScript } from '@chakra-ui/react';
import CookieConsentModal from './components/layout/CookieConsentDrawer';
import { UserProvider } from './contexts/UserContext';
import { SurveyProvider } from './contexts/SurveyContext';
import customTheme from './theme';

import LoginPage from './components/account/LoginPage';
import Website from './site/components/Website';
import NotFound from './pages/NotFound';
import ServerError from './pages/ServerError';
import QueryResetPasswordPage from './pages/ResetPasswordPage';
import Profile from './components/account/Profile';
import Preferences from './components/account/Preferences';
import ChangePassword from './components/account/ChangePassword';
import OrganizationRoutes from './components/organization/OrganizationRoutes';
import SurveyRoutes from './components/survey/SurveyRoutes';
import UserRoutes from './components/users/UserRoutes';
import MessagesList from './components/MessagesList';
import ProtectedRoute from './components/layout/ProtectedRoute';
import ProtectedLayout from './components/layout/ProtectedLayout';
import PublicSurveyRoutes from './components/public_surveys/PublicSurveyRoutes';
import ResourceArticle from './site/components/ResourceArticle';
import Dashboard from './components/Dashboard';
import StatusPage from './components/StatusPage';
import ResetPasswordRequestPage from './components/account/ResetPasswordRequestPage';
import ResetPasswordPage from './components/account/ResetPasswordPage';

// New Hub Imports
import OrgProtectedLayout from './components/hub/layout/OrgProtectedLayout';
import OrgDashboard from './components/hub/dashboard/OrgDashboard';
import OrgSurveys from './components/hub/surveys/OrgSurveys';
import OrgSettings from './components/hub/settings/OrgSettings';
import AdminRoutes from './components/admin/AdminRoutes';
import OrgProfile from './components/hub/profile/OrgProfile';
import SurveyConfigurationPage from './components/organization/SurveyConfigurationPage';
import OrgResults from './components/hub/results/OrgResults';
import ReportRoutes from './components/report/report_routes';
import { FeatureToggleProvider } from './contexts/FeatureToggleContext';
import SurveyPeriodRoutes from './components/survey_period/SurveyPeriodRoutes';
import ArticlesList from './components/articles/ArticlesList';
import ArticleCreate from './components/articles/ArticleCreate';
import ArticleEdit from './components/articles/ArticleEdit';
import ArticlePublicView from './components/articles/ArticlePublicView';
import ArticlePreviewPage from './components/articles/ArticlePreviewPage';
import EnvironmentBanner from './components/layout/EnvironmentBanner';
import WidgetAdminRoutes from './components/widgetAdmin/WidgetAdminRoutes';
import PublicConsultantLead from './pages/PublicConsultantLead';
import ConsultantRouteGuard from './components/layout/ConsultantRouteGuard';
import ConsultantAdminRouteGuard from './components/layout/ConsultantAdminRouteGuard';
import ConsultantDashboard from './components/consultant/ConsultantDashboard';
import ConsultantOrganizations from './components/consultant/ConsultantOrganizations';
import ConsultantOrganizationEdit from './components/consultant/ConsultantOrganizationEdit';
import ConsultantOrganizationView from './components/consultant/ConsultantOrganizationView';
import ConsultantReportRoutes from './components/consultant/ConsultantReportRoutes';
import ConsultantResultsPage from './components/consultant/ConsultantResultsPage';
import ConsultantNetworks from './components/consultant/ConsultantNetworks';
import ConsultantNetworkDetail from './components/consultant/ConsultantNetworkDetail';
import ConsultantSettingsPage from './components/consultant/ConsultantSettingsPage';
import NetworkRoutes from './components/network/NetworkRoutes';
import LeadsList from './components/leads/LeadsList';
import {
  consultantRequiredRoles,
  networkRequiredRoles,
  organizationRequiredRoles,
} from './utils/authRoles';
import OrganizationDashboardPage from './components/organization/OrganizationDashboardPage';
import OrganizationResultsPage from './components/organization/OrganizationResultsPage';
import OrganizationSurveysPage from './components/organization/OrganizationSurveysPage';
import OrganizationUsersPage from './components/organization/OrganizationUsersPage';
import WorkbookHubPage from './components/organization/workbook/WorkbookHubPage';
import WorkbookOverviewPage from './components/organization/workbook/WorkbookOverviewPage';
import WorkbookInterpretationPage from './components/organization/workbook/WorkbookInterpretationPage';
import WorkbookWidgetsPage from './components/organization/workbook/WorkbookWidgetsPage';

function App() {
  return (
    <ChakraProvider theme={customTheme}>
      <EnvironmentBanner />
      <FeatureToggleProvider>
        <ColorModeScript initialColorMode={customTheme.config.initialColorMode} />
        <Router>
          <CookieConsentModal />
          <Routes>
            {/* Public website route */}
            <Route path="/resources/school-effectiveness-survey" element={<ResourceArticle />} />
            <Route path="/article/:slug" element={<ArticlePublicView />} />
            <Route path="/" element={<Website />} />

            {/* Public surveys route */}
            <Route path="/survey/:endpoint/*" element={<PublicSurveyRoutes />} />

            {/* Public status page route */}
            <Route path="/status" element={<StatusPage />} />

            {/* Tour */}
            {/*
              <Route path="/tour" element={
                <UserProvider>
                  <StartTour />
                </UserProvider>
              } />
            */}

            <Route path="/tour" element={<Navigate to="/c/hke-tour" replace />} />
            <Route path="/c" element={<PublicConsultantLead />} />
            <Route path="/c/:consultantSlug/:advisorSlug" element={<PublicConsultantLead />} />
            <Route path="/c/:slug" element={<PublicConsultantLead />} />

            {/* Login route (wrapped in UserProvider) */}
            <Route path="/login" element={
              <UserProvider>
                <LoginPage />
              </UserProvider>
            } />

            <Route path="/password/forgot" element={<ResetPasswordRequestPage />} />
            <Route path="/password/reset/:token" element={<ResetPasswordPage />} />
            <Route path="/reset-password" element={<QueryResetPasswordPage />} />

            {/* 🔹 Admin/Internal routes - /app */}
            <Route path="/app" element={
              <UserProvider>
                <ProtectedRoute requiredRoles={["tech", "admin", "user"]}>
                  <ProtectedLayout />
                </ProtectedRoute>
              </UserProvider>
            }>
              <Route path="articles" element={<ArticlesList />} />
              <Route path="articles/new" element={<ArticleCreate />} />
              <Route path="articles/:id/edit" element={<ArticleEdit />} />
              <Route path="/app/articles/:id/preview" element={<ArticlePreviewPage />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="organizations/*" element={<OrganizationRoutes />} />
              <Route path="users/*" element={<UserRoutes />} />
              <Route path="messages" element={<MessagesList />} />
              <Route path="surveys/*" element={
                <SurveyProvider>
                  <SurveyRoutes />
                </SurveyProvider>
              } />
              <Route path="profile" element={<Profile />} />
              <Route path="settings" element={<Preferences />} />
              <Route path="password" element={<ChangePassword />} />
              <Route path="admin/*" element={<AdminRoutes />} />
              <Route path="results/*" element={<ReportRoutes isHub={false} />} />
              <Route path="survey_periods/*" element={<SurveyPeriodRoutes />} />
              <Route path="widget/*" element={<WidgetAdminRoutes />} />
            </Route>

            <Route path="/app/consultant" element={
              <UserProvider>
                <ProtectedRoute requiredRoles={consultantRequiredRoles}>
                  <ProtectedLayout />
                </ProtectedRoute>
              </UserProvider>
            }>
              <Route path="dashboard" element={
                <ConsultantRouteGuard>
                  <ConsultantDashboard />
                </ConsultantRouteGuard>
              } />
              <Route path="organizations" element={
                <ConsultantRouteGuard>
                  <ConsultantOrganizations />
                </ConsultantRouteGuard>
              } />
              <Route path="organizations/:id" element={
                <ConsultantRouteGuard>
                  <ConsultantOrganizationView />
                </ConsultantRouteGuard>
              } />
              <Route path="organizations/:id/edit" element={
                <ConsultantRouteGuard>
                  <ConsultantOrganizationEdit />
                </ConsultantRouteGuard>
              } />
              <Route path="organizations/:organizationId/results" element={
                <ConsultantAdminRouteGuard>
                  <ConsultantResultsPage />
                </ConsultantAdminRouteGuard>
              } />
              <Route path="organizations/:organizationId/results/*" element={
                <ConsultantAdminRouteGuard>
                  <ConsultantReportRoutes />
                </ConsultantAdminRouteGuard>
              } />
              <Route path="organizations/:organizationId/workbook" element={
                <ConsultantAdminRouteGuard>
                  <WorkbookHubPage />
                </ConsultantAdminRouteGuard>
              } />
              <Route path="organizations/:organizationId/workbook/:kind" element={
                <ConsultantAdminRouteGuard>
                  <WorkbookOverviewPage />
                </ConsultantAdminRouteGuard>
              } />
              <Route path="organizations/:organizationId/workbook/pages/:pageId" element={
                <ConsultantAdminRouteGuard>
                  <WorkbookInterpretationPage />
                </ConsultantAdminRouteGuard>
              } />
              <Route path="organizations/:organizationId/workbook/pages/:pageId/widgets" element={
                <ConsultantAdminRouteGuard>
                  <WorkbookWidgetsPage />
                </ConsultantAdminRouteGuard>
              } />
              <Route path="networks" element={
                <ConsultantRouteGuard>
                  <ConsultantNetworks />
                </ConsultantRouteGuard>
              } />
              <Route path="networks/:id" element={
                <ConsultantRouteGuard>
                  <ConsultantNetworkDetail />
                </ConsultantRouteGuard>
              } />
              <Route path="leads" element={
                <ConsultantRouteGuard>
                  <LeadsList />
                </ConsultantRouteGuard>
              } />
              <Route path="users/*" element={
                <ConsultantRouteGuard>
                  <UserRoutes
                    context={{ type: 'consultant' }}
                    basePath="/app/consultant/users"
                    title="Users"
                    description="Manage users within your consultant, network, and school scope."
                    scope="consultant"
                    allowGlobalCreate={true}
                  />
                </ConsultantRouteGuard>
              } />
              <Route path="consultant-settings" element={
                <ConsultantAdminRouteGuard>
                  <ConsultantSettingsPage />
                </ConsultantAdminRouteGuard>
              } />
              <Route path="profile" element={<Profile />} />
              <Route path="settings" element={<Preferences />} />
              <Route path="password" element={<ChangePassword />} />
            </Route>

            <Route path="/app/network/*" element={
              <UserProvider>
                <ProtectedRoute requiredRoles={networkRequiredRoles}>
                  <ProtectedLayout />
                </ProtectedRoute>
              </UserProvider>
            }>
              <Route path="*" element={<NetworkRoutes />} />
            </Route>

            <Route path="/app/organization" element={
              <UserProvider>
                <ProtectedRoute requiredRoles={organizationRequiredRoles}>
                  <ProtectedLayout />
                </ProtectedRoute>
              </UserProvider>
            }>
              <Route index element={<OrganizationDashboardPage />} />
              <Route path="users" element={<OrganizationUsersPage />} />
              <Route path="surveys" element={<OrganizationSurveysPage />} />
              <Route path="workbook" element={<WorkbookHubPage />} />
              <Route path="workbook/:kind" element={<WorkbookOverviewPage />} />
              <Route path="workbook/pages/:pageId" element={<WorkbookInterpretationPage />} />
              <Route path="workbook/pages/:pageId/widgets" element={<WorkbookWidgetsPage />} />
              <Route path="results" element={<OrganizationResultsPage />} />
              <Route
                path="results/*"
                element={<ReportRoutes isHub={false} basePathPrefix="/app/organization/results" />}
              />
              <Route path="profile" element={<Profile />} />
              <Route path="organization-settings" element={<OrgSettings />} />
              <Route path="settings" element={<Preferences />} />
              <Route path="password" element={<ChangePassword />} />
            </Route>

            {/* 🔹 Organization Hub routes - /hub */}
            <Route path="/hub" element={
              <UserProvider>
                <ProtectedRoute requiredRoles={["org_user", "tour"]}>
                  <OrgProtectedLayout />
                </ProtectedRoute>
              </UserProvider>
            }>
              <Route path="dashboard" element={<OrgDashboard />} />
              <Route path="surveys/configuration/:organizationSurveyId" element={<SurveyConfigurationPage />} />
              <Route path="surveys" element={<OrgSurveys />} />
              <Route path="settings" element={<OrgSettings />} />
              <Route path="password" element={<ChangePassword />} />
              <Route path="profile" element={<OrgProfile />} />
              <Route path="preferences" element={<Preferences />} />
              <Route path="results/*" element={<ReportRoutes isHub={true} />} />
              <Route path="results" element={<OrgResults />} />
            </Route>

            {/* Catch-all route for 404 */}
            <Route path="*" element={<NotFound />} />
            <Route path="/server-error" element={<ServerError />} />
          </Routes>
        </Router>
      </FeatureToggleProvider>
    </ChakraProvider>
  );
}

export default App;
